/**
 * Debounce function utility
 *
 * Creates a debounced version of a callback function that delays execution
 * until after the specified delay period has elapsed without being called again.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds (default: 300ms)
 * @returns A debounced version of the callback with a cancel method
 *
 * @example
 * ```typescript
 * const debouncedSave = debounce((value: string) => {
 *   api.save(value)
 * }, 500)
 *
 * // Call multiple times
 * debouncedSave('a')
 * debouncedSave('ab')
 * debouncedSave('abc') // Only this will execute after 500ms
 *
 * // Cleanup
 * debouncedSave.cancel()
 * ```
 */
export function debounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const debounced = function (this: any, ...args: Parameters<T>) {
    // Clear previous timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }

    // Set new timeout
    timeoutId = setTimeout(() => {
      callback.apply(this, args)
      timeoutId = null
    }, delay)
  } as T & { cancel: () => void }

  // Add cancel method to cleanup timeout
  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return debounced
}
